import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AppWindow, Contrast, LogOut, Mail } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Avatar } from '@/ui/avatar'
import { buttonVariants } from '@/ui/button-variants'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'

import { THEME_OPTIONS } from '@/shared/providers/theme-picker'
import { useTheme, type Theme } from '@/shared/providers/theme-provider'
import { clearToken, useAuth } from '@/lib/auth'
import { getMailpitUrl } from '@/lib/oidc'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

export function UserMenu() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { theme, setTheme, highContrast, setHighContrast } = useTheme()
  const [open, setOpen] = useState(false)

  const name = user?.name || 'Signed in'
  const email = user?.email ?? ''
  const mailpitUrl = getMailpitUrl()

  const onSignOut = async () => {
    try {
      if (await clearToken()) return true
    } catch (e) {
      console.error('[user-menu] sign out failed', e)
    }
    try {
      await navigate({ to: '/auth/login' })
      return true
    } catch {
      return false
    }
  }

  useAssistantBrowserCommand(assistantBrowserCommands.accountMenu, ({ open: desired }) => {
    setOpen(desired)
    return true
  })
  useAssistantBrowserCommand(assistantBrowserCommands.theme, ({ theme: desired }) => {
    if (desired !== 'light' && desired !== 'dark' && desired !== 'system') return false
    setTheme(desired)
    return true
  })
  useAssistantBrowserCommand(assistantBrowserCommands.highContrast, ({ enabled }) => {
    setHighContrast(enabled)
    return true
  })
  useAssistantBrowserCommand(assistantBrowserCommands.signOut, onSignOut)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={cn(buttonVariants({ variant: 'chrome', size: 'icon-lg' }))}
        aria-label="Account menu"
      >
        <span aria-hidden="true">
          <Avatar
            name={name}
            className="text-sidebar-foreground/75 size-6 bg-transparent text-[0.5625rem] ring-0"
          />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60 p-2">
        <div className="border-border flex items-center gap-2.5 border-b px-1 pb-2.5">
          <span aria-hidden="true">
            <Avatar name={name} className="size-9 text-[0.8125rem]" />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="text-foreground truncate text-sm font-medium">
              {name}
            </span>
            {email && (
              <span className="text-muted-foreground truncate text-xs">
                {email}
              </span>
            )}
          </div>
        </div>

        <DropdownMenuRadioGroup
          aria-label="Theme"
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
          className="pt-1"
        >
          {THEME_OPTIONS.map((opt) => (
            <DropdownMenuRadioItem key={opt.value} value={opt.value}>
              <opt.icon className="size-3.5" />
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuCheckboxItem
          checked={highContrast}
          onCheckedChange={setHighContrast}
        >
          <Contrast className="size-3.5" />
          High contrast
        </DropdownMenuCheckboxItem>

        {mailpitUrl && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLinkItem
              href={mailpitUrl}
              target="_blank"
              rel="noreferrer"
              closeOnClick
            >
              <Mail className="size-3.5" />
              Open Mailpit
            </DropdownMenuLinkItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void navigate({ to: '/me/connected-apps' })}>
          <AppWindow className="size-3.5" />
          Connected apps
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onClick={() => void onSignOut()}>
          <LogOut className="size-3.5" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
